import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Route names for type-safe navigation
export enum RouteName {
  Dashboard = 'dashboard',
  Profile = 'profile',
  Settings = 'settings',
  NotFound = 'not-found',
  AuthLogin = 'auth-login',
  AuthRegister = 'auth-register',
  AuthForgotPassword = 'auth-forgot-password',
  AuthResetPassword = 'auth-reset-password',
  AuthVerifyEmail = 'auth-verify-email',
  AuthConfirmEmailChange = 'auth-confirm-email-change',
  BillingPricing = 'billing-pricing',
  BillingSubscription = 'billing-subscription',
  Chat = 'chat',
}

// Layout types for route meta
export type LayoutType = 'default' | 'auth' | 'app'

// Extend RouteMeta to include layout
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresGuest?: boolean
    title?: string
    layout?: LayoutType
  }
}

const routes: RouteRecordRaw[] = [
  // Root redirect based on authentication state
  {
    path: '/',
    redirect: () => {
      const authStore = useAuthStore()
      return authStore.isAuthenticated ? '/dashboard' : '/auth/login'
    },
  },
  // Authenticated app routes with sidebar layout
  {
    path: '/dashboard',
    name: RouteName.Dashboard,
    component: () => import('@/pages/DashboardPage.vue'),
    meta: { requiresAuth: true, title: 'Dashboard', layout: 'app' },
  },
  {
    path: '/profile',
    name: RouteName.Profile,
    component: () => import('@/pages/ProfilePage.vue'),
    meta: { requiresAuth: true, title: 'Profile', layout: 'app' },
  },
  {
    path: '/settings',
    name: RouteName.Settings,
    component: () => import('@/pages/SettingsPage.vue'),
    meta: { requiresAuth: true, title: 'Settings', layout: 'app' },
  },
  // Auth routes
  {
    path: '/auth/login',
    name: RouteName.AuthLogin,
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { requiresGuest: true, title: 'Sign In', layout: 'auth' },
  },
  {
    path: '/auth/register',
    name: RouteName.AuthRegister,
    component: () => import('@/pages/auth/RegisterPage.vue'),
    meta: { requiresGuest: true, title: 'Create Account', layout: 'auth' },
  },
  {
    path: '/auth/forgot-password',
    name: RouteName.AuthForgotPassword,
    component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
    meta: { requiresGuest: true, title: 'Forgot Password', layout: 'auth' },
  },
  {
    path: '/auth/reset-password',
    name: RouteName.AuthResetPassword,
    component: () => import('@/pages/auth/ResetPasswordPage.vue'),
    meta: { requiresGuest: true, title: 'Reset Password', layout: 'auth' },
  },
  {
    path: '/auth/verify-email',
    name: RouteName.AuthVerifyEmail,
    component: () => import('@/pages/auth/EmailVerificationPage.vue'),
    meta: { requiresAuth: false, title: 'Verify Email', layout: 'auth' },
  },
  {
    // requiresAuth: false, exactly like /auth/verify-email above, and NOT
    // requiresGuest like /auth/reset-password. The link is opened from the new
    // mailbox, very often in the browser the user is already signed in with -
    // requiresGuest would bounce that visit to /dashboard (guard below) and
    // the link would silently never work for the most common case.
    path: '/auth/confirm-email-change',
    name: RouteName.AuthConfirmEmailChange,
    component: () => import('@/pages/auth/ConfirmEmailChangePage.vue'),
    meta: { requiresAuth: false, title: 'Confirm Email Change', layout: 'auth' },
  },
  // Billing routes
  {
    path: '/pricing',
    name: RouteName.BillingPricing,
    component: () => import('@/pages/billing/PricingPage.vue'),
    meta: { requiresAuth: false, title: 'Pricing', layout: 'default' },
  },
  {
    path: '/billing',
    name: RouteName.BillingSubscription,
    component: () => import('@/pages/billing/SubscriptionPage.vue'),
    meta: { requiresAuth: true, title: 'Subscription', layout: 'app' },
  },
  // Chat route
  {
    path: '/chat',
    name: RouteName.Chat,
    component: () => import('@/pages/ChatPage.vue'),
    meta: { requiresAuth: true, title: 'Chat', layout: 'app' },
  },
  // 404 - Must be last
  {
    path: '/:pathMatch(.*)*',
    name: RouteName.NotFound,
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { title: 'Page Not Found', layout: 'default' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.meta.requiresAuth
  const requiresGuest = to.meta.requiresGuest

  // Wait for auth initialization to complete before checking authentication
  // This prevents race conditions on page reload where the guard runs
  // before the auth state is restored from the refresh token cookie
  if (authStore.isInitializing) {
    await authStore.waitForInitialization()
  }

  // Check if route requires authentication
  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with return URL
    next({
      name: RouteName.AuthLogin,
      query: { redirect: to.fullPath },
    })
    return
  }

  // Check if route is for guests only (redirect authenticated users)
  if (requiresGuest && authStore.isAuthenticated) {
    // Get redirect from query or default to dashboard
    const redirect = (from.query.redirect as string) || '/dashboard'
    next(redirect)
    return
  }

  next()
})

export default router
